import { AssistantEvidenceItem, AssistantWebEvidence } from '@digital-twins/shared-types/assistant'

import { hostnameFromUrl } from '@/components/citations'

/** A path segment of five or more digits is an article id ("/articles/9237897-..."). */
const ARTICLE_ID_RE = /\d{5,}/
const BREADCRUMB_SEPARATOR_RE = /^\s*[›»>·|/]\s*/
/** "9237897Searching", "articlesChatGPT": a breadcrumb token glued to the title. */
const GLUED_TITLE_RE = new RegExp('^(\\S*?[\\p{Ll}\\d])(\\p{Lu}.*)$', 'u')
const SITE_SUFFIX_RE = /\s+[-|·–—]\s+[^-|·–—]+$/

/**
 * A search engine's url with an encoded fragment or a double-encoded suffix
 * glued on ("...searchgpt%23.eot", "...search%2528.zst") points at the page
 * before the junk. Anything that does not parse is returned unchanged.
 */
export function cleanWebUrl(url: string): string {
  try {
    const parsed = new URL(url)
    const junk = parsed.pathname.search(/%2[35]/i)
    if (junk >= 0) {
      parsed.pathname = parsed.pathname.slice(0, junk)
      parsed.search = ''
      parsed.hash = ''
    }
    return parsed.toString()
  } catch {
    return url
  }
}

/**
 * A result title without the search breadcrumb in front of it:
 * "help.example.com · articles · 12345How to search" reads "How to search".
 */
export function cleanWebTitle(title: string | null | undefined, hostname: string | null): string | null {
  let rest = (title ?? '').trim()
  if (!rest) {
    return null
  }
  const host = hostname?.toLowerCase() ?? null
  const lowered = rest.toLowerCase()
  const lead = host && lowered.startsWith(`www.${host}`) ? `www.${host}` : host
  if (!lead || !lowered.startsWith(lead)) {
    return rest
  }
  rest = rest.slice(lead.length)
  while (BREADCRUMB_SEPARATOR_RE.test(rest)) {
    rest = rest.replace(BREADCRUMB_SEPARATOR_RE, '')
    const token = rest.match(/^\S+/)?.[0] ?? ''
    const after = rest.slice(token.length)
    if (BREADCRUMB_SEPARATOR_RE.test(after)) {
      // A crumb ("articles") followed by the next separator.
      rest = after
      continue
    }
    const glued = token.match(GLUED_TITLE_RE)
    if (glued) {
      rest = `${glued[2]}${after}`
    }
    break
  }
  rest = rest.trim()
  return rest || null
}

function titleKey(hostname: string | null, title: string | null): string | null {
  if (!title) {
    return null
  }
  const core = title.replace(SITE_SUFFIX_RE, '').toLowerCase().replace(/\s+/g, ' ').trim()
  return core ? `${hostname ?? ''}|${core}` : null
}

function pageKey(url: string, hostname: string | null): string {
  try {
    const parsed = new URL(url)
    const id = parsed.pathname.match(ARTICLE_ID_RE)?.[0]
    return `${hostname ?? parsed.hostname}|${id ?? parsed.pathname.replace(/\/+$/, '').toLowerCase()}`
  } catch {
    return url
  }
}

/** Below this many distinct origins (web hostnames, or one per library/social source) there is no real surplus. */
export const MIN_FURTHER_READING_ORIGINS = 2

/**
 * Further reading rows a reader can use: web results with a clean url and
 * title, one per page (the same article under several urls or titles shows
 * once) and one per hostname. Library and social items pass through
 * unchanged, each its own origin. Empty unless at least two distinct origins
 * remain, so the section never lists one site's pages. Study and room items
 * pass through the same way.
 */
export function furtherReadingItems(items: Array<AssistantEvidenceItem> | null | undefined): Array<AssistantEvidenceItem> {
  const seen = new Set<string>()
  const hostnames = new Set<string>()
  const rows: Array<AssistantEvidenceItem> = []
  for (const item of items ?? []) {
    if (item.kind !== 'web') {
      if (item.kind === 'library' || item.kind === 'social' || item.kind === 'study' || item.kind === 'room') {
        rows.push(item)
      }
      continue
    }
    const url = cleanWebUrl(item.url)
    const hostname = hostnameFromUrl(url)?.toLowerCase() ?? null
    const title = cleanWebTitle(item.title, hostname)
    const keys = [pageKey(url, hostname), titleKey(hostname, title)].filter((key): key is string => key !== null)
    if (keys.some((key) => seen.has(key)) || (hostname !== null && hostnames.has(hostname))) {
      continue
    }
    keys.forEach((key) => seen.add(key))
    if (hostname !== null) {
      hostnames.add(hostname)
    }
    const cleaned: AssistantWebEvidence = { ...item, url, title }
    rows.push(cleaned)
  }
  // After the per-hostname cap every row is its own origin.
  return rows.length >= MIN_FURTHER_READING_ORIGINS ? rows : []
}
